import React from 'react';
import { FiCheckCircle, FiArrowRight, FiSmartphone, FiCode, FiZap, FiShield, FiTrendingUp, FiAward } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const AppDevelopmentPage = () => {
  useDocumentTitle(
    'Mobile App Development Services | End-to-End Android App Solutions',
    'Work with a mobile app development company in india that builds smooth, secure, and easy-to-use apps designed for real-world performance.'
  );

  const features = [
    {
      icon: FiSmartphone,
      title: "iOS & Android App Development",
      description: "We build apps for iPhones and Android phones so your customers can use them no matter what device they have"
    },
    {
      icon: FiCode,
      title: "Custom App Design",
      description: "We design apps that look professional, are easy to use, and match your brand perfectly"
    },
    {
      icon: FiZap,
      title: "App Store Launch & Setup",
      description: "We handle everything—getting your app approved and live on the App Store and Google Play Store"
    },
    {
      icon: FiShield,
      title: "Ongoing Updates & Support",
      description: "We keep your app updated, secure, and running smoothly with regular maintenance and support"
    }
  ];

  const pricingPlans = [
    {
      name: "Basic",
      price: "₹1,50,000",
      period: "",
      description: "Perfect for simple apps and MVPs",
      features: [
        "Single Platform (iOS or Android)",
        "Up to 10 Screens",
        "Basic UI/UX Design",
        "REST API Integration",
        "App Store Submission",
        "3 Months Support",
        "Source Code Included"
      ],
      highlighted: false
    },
    {
      name: "Professional",
      price: "₹3,00,000",
      period: "",
      description: "Ideal for feature-rich mobile applications",
      features: [
        "Cross-Platform (iOS & Android)",
        "Up to 25 Screens",
        "Custom UI/UX Design",
        "Advanced Features & Integrations",
        "Push Notifications",
        "Analytics Integration",
        "6 Months Support",
        "App Store Optimization"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For complex enterprise mobile solutions",
      features: [
        "Native or Cross-Platform",
        "Unlimited Screens",
        "Premium UI/UX Design",
        "Custom Backend Development",
        "Third-Party Integrations",
        "Security & Compliance",
        "12 Months Support",
        "Dedicated Development Team"
      ],
      highlighted: false
    }
  ];

  const whyChooseUs = [
    {
      title: "We build apps people actually use",
      description: "Not complicated or confusing—we design apps that are simple, intuitive, and enjoyable to use"
    },
    {
      title: "We handle the technical stuff—you focus on your business",
      description: "From design to launch and beyond, we take care of everything so you don't have to worry about the tech side"
    },
    {
      title: "Your app stays updated and supported",
      description: "We provide ongoing maintenance, updates, and support to keep your app running smoothly and securely"
    }
  ];

  const faqs = [
    {
      question: "How long does it take to develop a mobile app?",
      answer: "Timeline varies by complexity: Simple apps (Basic plan) take 2-3 months, feature-rich apps (Professional) need 4-6 months, and enterprise solutions can take 6-12 months. We provide detailed project timelines after understanding your requirements."
    },
    {
      question: "Should I choose native or cross-platform development?",
      answer: "Native (Swift/Kotlin) offers best performance and platform-specific features but costs more. Cross-platform (React Native/Flutter) is faster and more cost-effective, covering iOS and Android from one codebase. We'll recommend based on your app requirements, budget, and timeline."
    },
    {
      question: "What's included in the app development cost?",
      answer: "Our pricing includes UI/UX design, development, testing, App Store/Play Store submission, and initial support period. Additional costs may include backend development, third-party API integrations, and ongoing maintenance after the support period."
    },
    {
      question: "Do you provide ongoing maintenance after launch?",
      answer: "Yes! All plans include initial support (3-12 months based on plan). After that, we offer maintenance packages starting at ₹10,000/month covering updates, bug fixes, compatibility with new OS versions, and minor feature additions."
    },
    {
      question: "Can you help with App Store approval?",
      answer: "Absolutely! We handle the entire submission process for both Apple App Store and Google Play Store, including creating store listings, preparing screenshots, writing descriptions, and ensuring compliance with platform guidelines."
    },
    {
      question: "Will I own the source code?",
      answer: "Yes! Upon final payment, you receive complete ownership of the source code, design files, and all project assets. We can also provide documentation and knowledge transfer if you plan to maintain the app in-house."
    },
    {
      question: "Can you integrate payment gateways and third-party services?",
      answer: "Yes, we integrate various payment gateways (Razorpay, Stripe, PayPal), social media logins, analytics (Firebase, Google Analytics), push notifications, maps, and any other third-party APIs your app needs."
    },
    {
      question: "Do you offer App Store Optimization (ASO)?",
      answer: "Yes! Our Professional and Enterprise plans include ASO services—optimizing app title, description, keywords, screenshots, and ratings strategy to improve visibility and increase organic downloads from app stores."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = React.useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Mobile App Development
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              Mobile App Development helps you reach your customers wherever they are—on their phones. We create apps for iOS and Android that are easy to use, look great, and help your business grow
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#pricing"
                className="bg-white text-[#667eea] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
              >
                View Pricing
              </a>
              <a
                href="#contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#667eea] transition-colors inline-block"
              >
                Get Free Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-8 text-gray-900">
              What is Mobile App Development?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg leading-relaxed mb-6">
                Mobile app development is a simple and effective way to connect with your customers directly on their smartphones—whether they're using iPhones or Android devices. Think of it as building your own app (like Instagram or Uber) that people download and use to interact with your business. As the top mobile app development company in India, we design and build apps that are easy to use, look professional, and help your business reach more customers, generate leads, or sell products right from people's pockets.
              </p>
              <p className="text-lg leading-relaxed">
                You don't need to be a tech expert to have your own app. We handle everything—from designing how it looks to making sure it works smoothly on all devices. Whether you need a simple app for your store, a booking system, or a full-featured platform, we build it for you and guide you every step of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Our App Development Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-[#667eea] to-[#764ba2] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 text-[#667eea]">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            Transparent Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the perfect plan for your business needs. All plans include expert management and optimization.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-8 transform hover:scale-105 transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white shadow-2xl scale-105 border-4 border-[#667eea]'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="bg-white text-[#667eea] px-4 py-1 rounded-full text-sm font-semibold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#667eea]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-lg ${
                      plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                    }`}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#667eea]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-gray-100' : 'text-gray-700'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`block text-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-white text-[#667eea] hover:bg-gray-100'
                      : 'bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white hover:opacity-90'
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Choose Mirani Marketing for App Development?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-gradient-to-br from-[#667eea] to-[#764ba2] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#667eea]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-[#667eea] text-2xl">
                    {openFaqIndex === index ? '−' : '+'}
                  </span>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Build Your Mobile App?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-100">
            Get a free consultation and project quote. Let's turn your app idea into reality
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-[#667eea] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Get Free Quote
            </a>
            <a
              href="tel:+919876543210"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#667eea] transition-colors inline-block"
            >
              Call: +91 98765 43210
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AppDevelopmentPage;

